import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Link2, Loader2, Check, Unlink, RefreshCw, AlertTriangle, Lock, Building2, RotateCw } from 'lucide-react';

/**
 * LinkedIn Página/Organización — Sprint 70 (1/N). Community Management
 * API: identidad y conexión COMPLETAMENTE separadas del perfil personal
 * (LinkedInLoginConnect.js) — nunca la misma app, nunca el mismo token.
 * El mismo administrador de LinkedIn puede autorizar páginas distintas
 * para tenants distintos; la exclusividad se valida por página, nunca por
 * la persona.
 * @param {object} props
 * @param {function} [props.onConnected]
 * @param {boolean} [props.compact]
 */
const CAPABILITY_LABELS = {
  authorization: 'Autorización de LinkedIn',
  organization_listing: 'Listar páginas administradas',
  organization_selected: 'Página seleccionada',
  organization_posting: 'Publicar como la página',
  comment_read: 'Leer comentarios de la página',
  comment_reply: 'Responder comentarios como la página',
  pull_sync: 'Sincronización manual ("Sincronizar ahora")',
  real_time_webhooks: 'Comentarios en tiempo real (webhooks)',
  messages: 'Bandeja de mensajes real',
  ads: 'Anuncios de LinkedIn',
};

const REASON_LABELS = {
  missing_authorization: 'Falta autorizar o reconectar LinkedIn',
  no_organization_selected: 'Selecciona una página primero',
  limited_development_tier: 'LinkedIn aún no aprobó el Standard Tier — solo sincronización manual por ahora',
  subscription_not_active: 'Standard Tier aprobado, pero la suscripción de webhook aún no está activa — usa Sincronizar ahora mientras tanto',
  developer_application_urn_not_configured: 'Falta el Developer Application URN en Super Admin > Infraestructura',
  subscription_create_failed: 'LinkedIn rechazó la suscripción de webhook — vuelve a intentar o revisa la app',
  subscription_not_verified: 'La suscripción se creó pero no se pudo verificar como activa',
  missing_comment_feed_scopes: 'Faltan permisos de comentarios (r/w_organization_social_feed) — reautoriza',
  separate_partner_api_not_approved: 'Requiere Communications API + aprobación de partner, no configurada',
  separate_product_not_configured: 'Requiere Advertising API en la app separada "LinkedIn Ads", aún sin configurar',
};

export default function LinkedInPageConnect({ onConnected, compact = false }) {
  const [status, setStatus] = useState('loading'); // loading | not_connected | pending_selection | connected | error
  const [conn, setConn] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [orgsMessage, setOrgsMessage] = useState(null);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [reauthorizing, setReauthorizing] = useState(false);
  const [selecting, setSelecting] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [savingAiMode, setSavingAiMode] = useState(false);

  const loadAccount = useCallback(async () => {
    setStatus('loading');
    try {
      const { data } = await api.get('/tenant/oauth/linkedin-pages/account');
      if (data.connected) {
        setConn(data);
        setStatus('connected');
      } else if (data.pending_selection) {
        setConn(data);
        setStatus('pending_selection');
        await loadOrganizations();
      } else {
        setConn(data);
        setStatus('not_connected');
      }
    } catch (e) {
      setStatus('error');
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);

  const loadOrganizations = async () => {
    setLoadingOrgs(true);
    setOrgsMessage(null);
    try {
      const { data } = await api.get('/tenant/oauth/linkedin-pages/organizations');
      setOrgs(data.organizations || []);
      if (data.message) setOrgsMessage(data.message);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoadingOrgs(false);
    }
  };

  useEffect(() => { loadAccount(); }, [loadAccount]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const liPages = params.get('li_pages');
    if (liPages) {
      if (liPages === 'pending_selection') {
        loadAccount().then(() => onConnected?.());
      } else {
        const reason = params.get('reason');
        toast.error(
          reason === 'user_mismatch' || reason === 'invalid_state'
            ? 'La sesión de conexión ya no es válida — vuelve a intentarlo.'
            : 'No se pudo completar la conexión con LinkedIn Páginas — vuelve a intentarlo.'
        );
        setStatus('not_connected');
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('li_pages'); url.searchParams.delete('reason');
      window.history.replaceState({}, '', url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setRedirecting(true);
    try {
      const { data } = await api.get('/tenant/oauth/linkedin-pages/authorize', {
        params: { return_to: window.location.pathname },
      });
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo iniciar la conexión con LinkedIn Páginas');
      setRedirecting(false);
    }
  };

  const reauthorize = async () => {
    setReauthorizing(true);
    await connect();
  };

  const selectOrganization = async (organization_urn) => {
    setSelecting(organization_urn);
    try {
      await api.post('/tenant/oauth/linkedin-pages/select', { organization_urn });
      toast.success('Página de LinkedIn conectada');
      await loadAccount();
      onConnected?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === 'organization_already_connected') {
        toast.error('Esta página de LinkedIn ya está conectada a otra cuenta de CavernicolApp.');
      } else {
        toast.error(formatApiError(detail));
      }
    } finally {
      setSelecting(null);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('¿Desconectar la página de LinkedIn conectada?')) return;
    setDisconnecting(true);
    try {
      await api.post('/tenant/oauth/linkedin-pages/disconnect');
      toast.success('Página de LinkedIn desconectada');
      await loadAccount();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDisconnecting(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/tenant/oauth/linkedin-pages/sync');
      toast.success(`Sincronizado — ${data.ingested} comentario(s) nuevo(s) de ${data.notifications_seen} revisados`);
      await loadAccount();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === 'dev_tier_rate_limited') {
        toast.error(`Límite de LinkedIn alcanzado — vuelve a intentar en ${detail.retry_after_seconds}s`);
      } else {
        toast.error(formatApiError(detail));
      }
    } finally {
      setSyncing(false);
    }
  };

  const changeAiMode = async (mode) => {
    setSavingAiMode(true);
    try {
      const { data } = await api.put('/tenant/oauth/linkedin-pages/ai-mode', { mode });
      setConn(data);
      toast.success('Modo de IA para comentarios actualizado');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingAiMode(false);
    }
  };

  const [activatingRealtime, setActivatingRealtime] = useState(false);
  const activateRealtime = async () => {
    setActivatingRealtime(true);
    try {
      const { data } = await api.post('/tenant/oauth/linkedin-pages/activate-realtime');
      setConn(data);
      if (data?.capabilities?.real_time_webhooks?.available) {
        toast.success('Tiempo real activado — la suscripción de webhook quedó verificada');
      } else {
        toast.error('No se pudo activar tiempo real todavía — revisa el motivo mostrado abajo');
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setActivatingRealtime(false);
    }
  };

  if (status === 'loading') {
    return <div className="flex items-center gap-2 text-sm text-[#64748B] py-2"><Loader2 className="w-4 h-4 animate-spin" /> Buscando tu página de LinkedIn...</div>;
  }

  if (status === 'error') {
    return (
      <div className={compact ? '' : 'bg-white border border-[#E2E8F0] rounded-xl p-5'}>
        <Button onClick={loadAccount} variant="outline" size="sm" className="gap-2"><RefreshCw className="w-4 h-4" /> Reintentar</Button>
      </div>
    );
  }

  if (status === 'not_connected') {
    return (
      <div className={compact ? '' : 'bg-white border border-[#E2E8F0] rounded-xl p-5'}>
        <p className="text-sm text-[#64748B] mb-3">
          Conecta una LinkedIn Page que administres para publicar como la marca y responder comentarios de sus
          publicaciones. Esto es distinto de tu conexión de LinkedIn personal — nunca comparte cuenta ni token.
          Mensajes reales y anuncios de LinkedIn son pasos futuros separados.
        </p>
        <Button onClick={connect} disabled={redirecting} className="gap-2" data-testid="linkedin-page-connect-button">
          {redirecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
          Conectar páginas de LinkedIn
        </Button>
      </div>
    );
  }

  if (status === 'pending_selection') {
    return (
      <div className={compact ? '' : 'bg-white border border-[#E2E8F0] rounded-xl p-5'} data-testid="linkedin-page-selector">
        <p className="text-sm font-semibold text-[#0F172A] mb-1">Elige la página que quieres conectar</p>
        <p className="text-xs text-[#64748B] mb-3">
          Estas son las LinkedIn Pages donde la cuenta autorizada tiene rol de Administrator.
        </p>
        {loadingOrgs ? (
          <div className="flex items-center gap-2 text-sm text-[#64748B] py-2"><Loader2 className="w-4 h-4 animate-spin" /> Consultando páginas administradas...</div>
        ) : orgsMessage ? (
          <div className="flex items-start gap-2 border border-amber-200 bg-amber-50 rounded-lg p-3 text-xs text-amber-800">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{orgsMessage}</span>
          </div>
        ) : (
          <div className="space-y-2">
            {orgs.map(org => (
              <div key={org.organization_urn} className="flex items-center justify-between border border-[#E2E8F0] rounded-lg px-3 py-2 bg-[#F8FAFC]">
                <div className="flex items-center gap-2">
                  {org.logo_url ? (
                    <img src={org.logo_url} alt="" className="w-6 h-6 rounded" />
                  ) : (
                    <div className="w-6 h-6 rounded bg-[#E2E8F0] flex items-center justify-center"><Building2 className="w-3.5 h-3.5 text-[#94A3B8]" /></div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-[#0F172A]">{org.name || org.organization_id}</p>
                    {org.vanity_name && <p className="text-[11px] text-[#94A3B8]">linkedin.com/company/{org.vanity_name}</p>}
                  </div>
                </div>
                <Button size="sm" onClick={() => selectOrganization(org.organization_urn)} disabled={selecting === org.organization_urn}
                  data-testid={`linkedin-page-select-${org.organization_id}`}>
                  {selecting === org.organization_urn ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Conectar esta página'}
                </Button>
              </div>
            ))}
          </div>
        )}
        <Button variant="outline" size="sm" className="mt-3 gap-2" onClick={loadOrganizations} disabled={loadingOrgs}>
          <RefreshCw className="w-3.5 h-3.5" /> Volver a consultar
        </Button>
      </div>
    );
  }

  const expiresAt = conn?.expires_at ? new Date(conn.expires_at) : null;
  const needsReauth = conn?.status === 'expired' || conn?.status === 'expiring_soon';
  // "Standard Tier aprobado" is only a Super Admin manual checklist entry —
  // it never means webhooks are actually live. Only a verified, active
  // subscription does, so "tiempo real" is only ever claimed from THIS
  // flag, never from the tier checkbox alone (Sincronizar ahora also never
  // gets hidden just because this happens to be true — a manual sync is
  // still a valid fallback even with real-time active).
  const realTimeActive = conn?.capabilities?.real_time_webhooks?.available;
  const cooldownSeconds = conn?.dev_tier_cooldown_seconds || 0;
  const aiMode = conn?.comments_ai_mode || 'disabled';

  return (
    <div className={compact ? '' : 'bg-white border border-[#E2E8F0] rounded-xl p-5'} data-testid="linkedin-page-connected">
      {needsReauth && (
        <div className={`flex flex-col gap-2 border rounded-lg px-3 py-2 mb-3 text-xs ${conn.status === 'expired' ? 'border-red-200 bg-red-50 text-red-900' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              {conn.status === 'expired'
                ? 'Tu conexión de la página de LinkedIn venció. Reautoriza para seguir publicando y respondiendo comentarios.'
                : `Tu conexión vence pronto (${expiresAt ? expiresAt.toLocaleDateString() : 'en unos días'}). Reautoriza antes de que expire.`}
            </span>
          </div>
          <Button size="sm" variant="outline" className="h-7 text-xs self-start" onClick={reauthorize}
            disabled={reauthorizing || redirecting} data-testid="linkedin-page-reauthorize-button">
            {(reauthorizing || redirecting) ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Reautorizar LinkedIn Páginas
          </Button>
        </div>
      )}
      <div className="flex items-center justify-between border border-[#E2E8F0] rounded-lg px-3 py-2 mb-3 bg-[#F8FAFC]">
        <div className="flex items-center gap-2">
          {conn?.logo_url ? (
            <img src={conn.logo_url} alt="" className="w-6 h-6 rounded" />
          ) : (
            <div className="w-6 h-6 rounded bg-[#E2E8F0] flex items-center justify-center"><Building2 className="w-3.5 h-3.5 text-[#94A3B8]" /></div>
          )}
          <div>
            <p className="text-sm font-medium text-[#0F172A] flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5 text-emerald-600" /> {conn?.organization_name || 'Página conectada'}
            </p>
            <p className="text-[11px] text-[#94A3B8]">
              {expiresAt ? `Vence el ${expiresAt.toLocaleDateString()}` : 'Sin fecha de vencimiento registrada'}
              {conn?.has_refresh_token === false && ' — sin renovación automática'}
              {' — '}{realTimeActive ? 'Tiempo real activo (suscripción verificada)' : 'Sincronización manual'}
            </p>
            {conn?.reauthorization_pending && (
              <p className="text-[11px] text-amber-700 mt-0.5">Hay una reautorización en curso — la conexión actual sigue funcionando hasta que confirmes la nueva página.</p>
            )}
          </div>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={syncNow} disabled={syncing || cooldownSeconds > 0}
            title={cooldownSeconds > 0 ? `Límite alcanzado — espera ${cooldownSeconds}s` : 'Sincronizar ahora'}>
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
          </Button>
          {!needsReauth && (
            <Button size="sm" variant="outline" onClick={reauthorize} disabled={reauthorizing || redirecting} title="Cambiar página / reautorizar">
              {(reauthorizing || redirecting) ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={disconnect} disabled={disconnecting} title="Desconectar">
            {disconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>
      <div className="border border-[#E2E8F0] rounded-lg px-3 py-2">
        <p className="text-xs font-semibold text-[#0F172A] mb-2">Qué puede hacer esta conexión hoy</p>
        <div className="space-y-1.5">
          {Object.entries(conn?.capabilities || {}).map(([key, cap]) => (
            <div key={key} className="flex items-start gap-2 text-xs">
              {cap.available
                ? <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                : <Lock className="w-3.5 h-3.5 text-[#CBD5E1] mt-0.5 shrink-0" />}
              <div>
                <span className={cap.available ? 'text-[#0F172A]' : 'text-[#94A3B8]'}>{CAPABILITY_LABELS[key] || key}</span>
                {!cap.available && cap.reason && (
                  <span className="text-[#CBD5E1]"> — {REASON_LABELS[cap.reason] || cap.reason}</span>
                )}
              </div>
            </div>
          ))}
        </div>
        {conn?.can_retry_realtime && (
          <div className="mt-2 pt-2 border-t border-[#E2E8F0]">
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5" onClick={activateRealtime}
              disabled={activatingRealtime} data-testid="linkedin-page-activate-realtime">
              {activatingRealtime ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
              Activar/Reintentar tiempo real
            </Button>
            <p className="text-[11px] text-[#94A3B8] mt-1">
              Esta conexión se hizo antes de que la suscripción de webhook quedara lista — no hace falta reconectar
              LinkedIn, solo reintentar la suscripción.
            </p>
          </div>
        )}
        {conn?.subscription_expires_at && (
          <p className="text-[11px] text-[#94A3B8] mt-2 pt-2 border-t border-[#E2E8F0]">
            Suscripción de tiempo real vence el {new Date(conn.subscription_expires_at).toLocaleDateString()}
            {conn?.subscription_last_verified_at && ` — última verificación ${new Date(conn.subscription_last_verified_at).toLocaleString()}`}
          </p>
        )}
      </div>
      <div className="border border-[#E2E8F0] rounded-lg px-3 py-2 mt-3">
        <p className="text-xs font-semibold text-[#0F172A] mb-1">IA responde comentarios de LinkedIn</p>
        <p className="text-xs text-[#64748B] mb-2">
          Por defecto desactivada. "Sugerir" te muestra una respuesta propuesta que envías tú manualmente desde
          Conversaciones; "Automática" hace que la IA responda públicamente sola, como la página.
        </p>
        <div className="flex gap-1.5">
          {[
            { value: 'disabled', label: 'Desactivada' },
            { value: 'suggest', label: 'Sugerir' },
            { value: 'auto', label: 'Automática' },
          ].map(opt => (
            <button key={opt.value} type="button" disabled={savingAiMode} onClick={() => changeAiMode(opt.value)}
              className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors ${
                aiMode === opt.value
                  ? 'bg-[#0F172A] text-white border-[#0F172A]'
                  : 'bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#94A3B8]'
              }`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
